import { faCrown } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import Icon from 'components/Icon';

import styles from './leaderboard.module.scss';
import confetti from 'canvas-confetti';
import cx from 'classnames';

type TUser = { name: string; count: number; rank: number; id?: number | null };

// Indexed by standing (0 = winner) rather than rank so tied users still get
// distinct colours on the podium.
const STANDINGS = [
  { avatar: '#38d0f5', bar: 'linear-gradient(180deg, #5ee0ff, #1f9ec2)' },
  { avatar: '#6f83ff', bar: 'linear-gradient(180deg, #7c8dff, #4a5fd0)' },
  { avatar: '#a684ff', bar: 'linear-gradient(180deg, #a684ff, #7c5cff)' }
];
const RUNNER_UP_AVATARS = ['#35d0a0', '#5be584'];

// Keeps last place tall enough to hold its rank label when the winner is far ahead.
const MIN_BAR_FRACTION = 0.5;

// Runners-up rise first and the winner lands last, so the podium builds towards
// the centre rather than snapping into place all at once. Indexed by standing.
const STANDING_DELAYS = [220, 0, 110];
const ROW_DELAY = 340;
const ROW_STAGGER = 90;
const PROMOTION_HIGHLIGHT_DURATION = 60_000;
const CELEBRATION_REPEAT_OFFSETS = [0, 20_000, 40_000];
const CELEBRATION_PARTICLE_COUNT = 55;
const CELEBRATION_SPREAD = 75;
const CONFETTI_COLORS = ['#ffd700', '#ff4d6d', '#00e5ff', '#7cff6b', '#ff8c00', '#ffffff', '#d65cff'];
const fireConfetti = confetti.create(undefined, {
  resize: true,
  useWorker: true,
  disableForReducedMotion: true
});

const celebrate = () => {
  if (document.hidden) {
    return;
  }

  const shared = {
    colors: CONFETTI_COLORS,
    particleCount: CELEBRATION_PARTICLE_COUNT,
    spread: CELEBRATION_SPREAD,
    startVelocity: 45,
    gravity: 1,
    scalar: 0.95,
    ticks: 160,
    disableForReducedMotion: true
  };

  fireConfetti({ ...shared, angle: 60, origin: { x: 0, y: 1 } });
  fireConfetti({ ...shared, angle: 120, origin: { x: 1, y: 1 } });
};

const getNumberSuffix = (value: number) => {
  const remainder = Math.abs(value) % 100;

  if (remainder >= 11 && remainder <= 13) {
    return 'th';
  }

  switch (remainder % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const getInitial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';
const getUserKey = ({ id, name }: Pick<TUser, 'id' | 'name'>) => (id == null ? name : String(id));

const AVATAR_IMAGE_SIZE = 256;

const getAvatarUrl = (id?: number | null) =>
  id === null || id === undefined
    ? undefined
    : `https://avatars.githubusercontent.com/u/${id}?s=${AVATAR_IMAGE_SIZE}&v=4`;

function ReviewerAvatar({
  user,
  className,
  style,
  promoted = false,
  glowColor,
  children
}: {
  user: TUser;
  className: string;
  style: CSSProperties;
  promoted?: boolean;
  glowColor?: string;
  children?: ReactNode;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const avatarUrl = getAvatarUrl(user.id);

  return (
    <div
      className={cx(className, {
        [styles['leaderboard__avatar--promoted']]: promoted
      })}
      style={{ ...style, '--avatar-glow': glowColor } as CSSProperties}
    >
      <span
        className={cx(styles['leaderboard__avatar-initial'], {
          [styles['leaderboard__avatar-initial--loaded']]: loaded
        })}
      >
        {getInitial(user.name)}
      </span>
      {children}
      {avatarUrl && !failed && (
        <img
          className={cx(styles['leaderboard__avatar-image'], {
            [styles['leaderboard__avatar-image--loaded']]: loaded
          })}
          src={avatarUrl}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

function PodiumColumn({
  user,
  standing,
  topCount,
  promoted
}: {
  user: TUser;
  standing: number;
  topCount: number;
  promoted: boolean;
}) {
  const { avatar, bar } = STANDINGS[standing];
  const fraction = Math.max(MIN_BAR_FRACTION, topCount ? user.count / topCount : 1);
  const delay = `${STANDING_DELAYS[standing]}ms`;

  return (
    <div className={styles['leaderboard__column']}>
      {/* Takes the share of the column the bar leaves behind, so the meta block rides its own bar */}
      <span className={styles['leaderboard__lift']} style={{ flexGrow: 1 - fraction }} aria-hidden="true" />
      <div className={styles['leaderboard__meta']} style={{ animationDelay: delay }}>
        <ReviewerAvatar
          user={user}
          className={styles['leaderboard__avatar']}
          style={{ background: avatar, boxShadow: `0 10px 28px ${avatar}66` }}
          promoted={promoted}
          glowColor={avatar}
        >
          {standing === 0 && (
            <Icon className={styles['leaderboard__crown']} icon={faCrown} color="auroraAmber" size={26} />
          )}
        </ReviewerAvatar>
        <span className={styles['leaderboard__name']} title={user.name}>
          {user.name}
        </span>
        <span className={styles['leaderboard__count']}>{user.count}</span>
      </div>
      <div className={styles['leaderboard__track']} style={{ flexGrow: fraction }}>
        <div className={styles['leaderboard__bar']}>
          {/* The fill carries the grow animation so the rank label never squashes with it */}
          <span
            className={styles['leaderboard__bar-fill']}
            style={{ background: bar, boxShadow: `0 16px 40px ${avatar}3d`, animationDelay: delay }}
          />
          <span
            className={cx('font-display', styles['leaderboard__rank'])}
            // Trails its own bar so the label lands as the bar tops out
            style={{ animationDelay: `${STANDING_DELAYS[standing] + 160}ms` }}
          >
            {user.rank}
            <small>{getNumberSuffix(user.rank)}</small>
          </span>
        </div>
      </div>
    </div>
  );
}

function addRankToUsers(users: { name: string; count: number; id?: number | null }[]): TUser[] {
  // Sort the array by count in descending order (name breaks ties so the order is stable) and create an array of
  // unique counts
  const sortedArray = users.slice().sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const uniqueCounts = [...new Set(sortedArray.map((item) => item.count))];

  // Create a map of ranks based on the unique counts
  const rankMap = Object.fromEntries(uniqueCounts.map((count, index) => [count, index + 1]));

  // Map each ranked item to include its rank from the rankMap
  return sortedArray.map((item) => ({
    ...item,
    rank: rankMap[item.count] as number
  }));
}

export default function Leaderboard({ data }: { data: { name: string; count: number; id?: number | null }[] }) {
  const users = addRankToUsers(data);
  const rankSignature = users.map((user) => `${getUserKey(user)}:${user.rank}`).join('|');
  // Values as well as order, so the entrance animation replays on a real change but not on an identical refetch
  const valueSignature = users.map(({ name, count }) => `${name}:${count}`).join('|');
  const previousRanks = useRef<Map<string, number>>(new Map());
  const celebrationTimers = useRef<number[]>([]);
  const promotionTimers = useRef<Map<string, number>>(new Map());
  const [promotedUsers, setPromotedUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    const hadPreviousRanks = previousRanks.current.size > 0;
    const promoted = hadPreviousRanks
      ? users.filter((user) => {
          const previousRank = previousRanks.current.get(getUserKey(user));

          // A new top-five entrant has also moved up into the visible leaderboard.
          return previousRank === undefined || user.rank < previousRank;
        })
      : [];

    previousRanks.current = new Map(users.map((user) => [getUserKey(user), user.rank]));

    if (promoted.length > 0) {
      const promotedKeys = promoted.map(getUserKey);

      setPromotedUsers((current) => new Set([...current, ...promotedKeys]));
      promotedKeys.forEach((key) => {
        const existingTimer = promotionTimers.current.get(key);

        if (existingTimer !== undefined) {
          window.clearTimeout(existingTimer);
        }

        const timer = window.setTimeout(() => {
          setPromotedUsers((current) => {
            const next = new Set(current);
            next.delete(key);
            return next;
          });
          promotionTimers.current.delete(key);
        }, PROMOTION_HIGHLIGHT_DURATION);

        promotionTimers.current.set(key, timer);
      });

      celebrationTimers.current.forEach((timer) => window.clearTimeout(timer));
      celebrationTimers.current = CELEBRATION_REPEAT_OFFSETS.map((offset) => window.setTimeout(celebrate, offset));
    }
  }, [rankSignature]);

  useEffect(() => {
    return () => {
      celebrationTimers.current.forEach((timer) => window.clearTimeout(timer));
      promotionTimers.current.forEach((timer) => window.clearTimeout(timer));
      fireConfetti.reset();
    };
  }, []);

  if (users.length < 3) {
    return (
      <p className={styles['leaderboard__empty']}>Not enough code reviews completed to display the leaderboard.</p>
    );
  }

  const topCount = users[0].count;
  // Silver on the left, gold centre, bronze right — the standing drives the
  // colour so the display order can stay podium-shaped.
  const podium = [
    { user: users[1], standing: 1 },
    { user: users[0], standing: 0 },
    { user: users[2], standing: 2 }
  ];
  const runnersUp = users.slice(3, 5);

  return (
    <div className={styles.leaderboard} key={valueSignature}>
      <div className={styles['leaderboard__podium']}>
        {podium.map(({ user, standing }) => (
          <PodiumColumn
            key={user.name}
            user={user}
            standing={standing}
            topCount={topCount}
            promoted={promotedUsers.has(getUserKey(user))}
          />
        ))}
      </div>
      {runnersUp.length > 0 && (
        <div className={styles['leaderboard__rows']}>
          {runnersUp.map((user, index) => (
            <div
              className={styles['leaderboard__row']}
              key={user.name}
              style={{ animationDelay: `${ROW_DELAY + index * ROW_STAGGER}ms` }}
            >
              <span className={cx('font-display', styles['leaderboard__row-rank'])}>{user.rank}</span>
              <ReviewerAvatar
                user={user}
                className={styles['leaderboard__row-avatar']}
                style={{ background: RUNNER_UP_AVATARS[index % RUNNER_UP_AVATARS.length] }}
                promoted={promotedUsers.has(getUserKey(user))}
                glowColor={RUNNER_UP_AVATARS[index % RUNNER_UP_AVATARS.length]}
              />
              <span className={styles['leaderboard__row-name']} title={user.name}>
                {user.name}
              </span>
              <span className={cx('font-display', styles['leaderboard__row-count'])}>{user.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
